/**
 * CallerIdProtection.jsx
 *
 * STIR/SHAKEN and spam protection setup guidance component.
 * Displays current attestation level, setup guide, benefits, and Voice Integrity status.
 */

import { useState } from 'react';
import { Shield, Phone, CheckCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { theme } from '../../lib/theme';

const card = {
  background: theme.colors.card,
  borderRadius: theme.radius.lg,
  border: `1px solid ${theme.colors.border}`,
  padding: 24,
  marginBottom: 20,
};

const sectionTitle = {
  fontSize: 15,
  fontWeight: 600,
  color: theme.colors.text,
  marginBottom: 12,
};

const stepContainer = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '12px 0',
  borderBottom: `1px solid ${theme.colors.border}`,
};

const stepNumber = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: `${theme.colors.primary}12`,
  color: theme.colors.primary,
  fontSize: 13,
  fontWeight: 700,
  flexShrink: 0,
};

const stepContent = {
  flex: 1,
  minWidth: 0,
};

const stepLabel = {
  fontSize: 13,
  fontWeight: 500,
  color: theme.colors.text,
  marginBottom: 2,
};

const stepDescription = {
  fontSize: 12,
  color: theme.colors.textLight,
};

const link = {
  color: theme.colors.primary,
  textDecoration: 'none',
  cursor: 'pointer',
  fontWeight: 500,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const benefitBox = {
  padding: 16,
  borderRadius: theme.radius.md,
  background: `${theme.colors.info}08`,
  border: `1px solid ${theme.colors.info}20`,
  marginTop: 12,
};

const benefitText = {
  fontSize: 13,
  color: theme.colors.text,
  lineHeight: 1.5,
};

const statusBadge = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
};

const checkButton = {
  padding: '8px 16px',
  background: theme.colors.primary,
  color: '#fff',
  border: 'none',
  borderRadius: theme.radius.sm,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 16,
};

export default function CallerIdProtection() {
  const [status, setStatus] = useState('Not configured'); // A, B, C, or "Not configured"
  const [checking, setChecking] = useState(false);

  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      // Stub API call to /api/twilio-status
      const response = await fetch('/api/twilio-status', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data.attestation_level || 'Not configured');
      }
    } catch (err) {
      console.error('Failed to check status:', err);
    }
    setChecking(false);
  };

  const getStatusColor = () => {
    if (status === 'A') return { bg: `${theme.colors.success}14`, color: theme.colors.success };
    if (status === 'B') return { bg: `${theme.colors.warning}14`, color: theme.colors.warning };
    if (status === 'C') return { bg: `${theme.colors.danger}14`, color: theme.colors.danger };
    return { bg: `${theme.colors.textLight}14`, color: theme.colors.textLight };
  };

  const statusColor = getStatusColor();

  return (
    <div>
      {/* Status Section */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Shield size={20} color={theme.colors.primary} />
          <h2 style={{ fontSize: 17, fontWeight: 600, color: theme.colors.text, margin: 0 }}>
            Caller ID Protection
          </h2>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: theme.colors.textLight }}>
            STIR/SHAKEN Attestation Level:
          </span>
          <span style={{ ...statusBadge, ...statusColor }}>
            {status === 'A' && <CheckCircle size={14} />}
            {status === 'B' && <AlertTriangle size={14} />}
            {status === 'C' && <AlertTriangle size={14} />}
            {(status === 'A' || status === 'B' || status === 'C') ? `Level ${status}` : status}
          </span>
        </div>

        <p style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 12 }}>
          {status === 'A' && 'Your Trovva phone numbers are verified and showing A-level attestation to carriers.'}
          {status === 'B' && 'Your Trovva phone numbers have intermediate attestation. Complete Trust Hub verification for A-level.'}
          {status === 'C' && 'Your Trovva phone numbers have basic attestation. Verify your business identity for higher attestation.'}
          {status === 'Not configured' && 'STIR/SHAKEN is not yet configured. Follow the setup guide below to get started.'}
        </p>

        <button onClick={handleCheckStatus} disabled={checking} style={{ ...checkButton, opacity: checking ? 0.6 : 1 }}>
          <Phone size={14} />
          {checking ? 'Checking...' : 'Check Status'}
        </button>
      </div>

      {/* Setup Guide */}
      <div style={card}>
        <h3 style={sectionTitle}>Setup Guide</h3>
        <p style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 16 }}>
          Follow these 5 steps to enable STIR/SHAKEN A-level attestation for your brokerage phone numbers.
        </p>

        {/* Step 1 */}
        <div style={stepContainer}>
          <div style={stepNumber}>1</div>
          <div style={stepContent}>
            <div style={stepLabel}>
              <a
                href="https://console.twilio.com/us1/develop/auth/trust-hub"
                target="_blank"
                rel="noreferrer"
                style={link}
              >
                Verify your business identity in Twilio Trust Hub
                <ExternalLink size={12} />
              </a>
            </div>
            <div style={stepDescription}>
              Go to Twilio Console and navigate to Trust Hub. This is where you'll manage your phone number authentication.
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div style={stepContainer}>
          <div style={stepNumber}>2</div>
          <div style={stepContent}>
            <div style={stepLabel}>Create a Business Profile with your brokerage's legal name, EIN, and address</div>
            <div style={stepDescription}>
              Enter your brokerage's full legal name as it appears on your business registration, your Employer Identification Number (EIN), and your brokerage's physical business address.
            </div>
          </div>
        </div>

        {/* Step 3 */}
        <div style={stepContainer}>
          <div style={stepNumber}>3</div>
          <div style={stepContent}>
            <div style={stepLabel}>Wait 24–72 hours for Twilio to verify your profile</div>
            <div style={stepDescription}>
              Twilio's verification team reviews your business documents and identity. You'll receive an email notification once approved.
            </div>
          </div>
        </div>

        {/* Step 4 */}
        <div style={stepContainer}>
          <div style={stepNumber}>4</div>
          <div style={stepContent}>
            <div style={stepLabel}>Assign your Trovva phone numbers to the SHAKEN/STIR Trust Product</div>
            <div style={stepDescription}>
              Once your business profile is verified, link all your Trovva-issued phone numbers to the SHAKEN/STIR Trust Product in Twilio Trust Hub.
            </div>
          </div>
        </div>

        {/* Step 5 */}
        <div style={{ ...stepContainer, borderBottom: 'none' }}>
          <div style={stepNumber}>5</div>
          <div style={stepContent}>
            <div style={stepLabel}>Your outbound calls will now show A-level attestation (verified business)</div>
            <div style={stepDescription}>
              Once configured, all outbound calls from your assigned numbers will display as verified to recipients' phones. Carriers trust A-level attestation and are far less likely to flag your calls as spam.
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div style={card}>
        <h3 style={sectionTitle}>Why STIR/SHAKEN Matters</h3>
        <div style={benefitBox}>
          <div style={benefitText}>
            <strong>A-level attestation tells carriers your calls are legitimate.</strong> This prevents your agents' calls from being flagged as "Spam Likely" on AT&T, T-Mobile, and other carriers. Prospects answer more reliably, and your agents spend less time calling back blocked numbers.
          </div>
        </div>
      </div>

      {/* Voice Integrity Section */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Shield size={20} color={theme.colors.primary} />
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: 0 }}>
            Voice Integrity Monitoring
          </h3>
        </div>
        <p style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 12 }}>
          After STIR/SHAKEN setup, enable <strong>Voice Integrity</strong> in Twilio Trust Hub to actively monitor and remediate spam labels on your numbers. Voice Integrity automatically challenges and removes false spam flags that may appear despite your A-level attestation.
        </p>
        <div style={{
          padding: 12,
          borderRadius: theme.radius.sm,
          background: `${theme.colors.info}08`,
          border: `1px solid ${theme.colors.info}20`,
          fontSize: 12,
          color: theme.colors.text,
        }}>
          <strong>Status:</strong> Voice Integrity is configured separately in Twilio Trust Hub. Check your Trust Hub dashboard for the current status.
        </div>
      </div>
    </div>
  );
}
